import { useCallback, useEffect, useRef, useState } from "react";
import type { OptimizerInput, OptimizerOutput } from "../types";
import type {
  WorkerRequest,
  WorkerReply,
} from "../workers/optimizer.worker";

export function useOptimizer() {
  const workerRef = useRef<Worker | null>(null);
  const latestIdRef = useRef<string | null>(null);
  const [result, setResult] = useState<OptimizerOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    const worker = new Worker(
      new URL("../workers/optimizer.worker.ts", import.meta.url),
      { type: "module" },
    );
    worker.onmessage = (e: MessageEvent<WorkerReply>) => {
      // Drop stale replies (a newer run() has fired since)
      if (e.data.id !== latestIdRef.current) return;

      setIsRunning(false);
      if (e.data.ok) {
        setResult(e.data.output);
        setError(null);
      } else {
        setError(e.data.error);
      }
    };
    workerRef.current = worker;
    return () => worker.terminate();
  }, []);

  const run = useCallback((input: OptimizerInput) => {
    if (!workerRef.current) return;
    const id = crypto.randomUUID();
    latestIdRef.current = id;
    setIsRunning(true);
    setError(null);
    const req: WorkerRequest = { id, input };
    workerRef.current.postMessage(req);
  }, []);

  return { run, result, error, isRunning };
}
